import type { OCGTerritory } from './data-typing';

export class BiomarkerAHAData {
  private biomarkerName: string;
  private data: number[];
  private sliceIndex: string;
  private range: [number, number] | undefined;

  constructor(
    biomarkerName: string,
    data: number[],
    sliceIndex: string,
    range?: [number, number] | null,
  ) {
    this.biomarkerName = biomarkerName;
    this.data = [...data];
    this.sliceIndex = sliceIndex;
    this.range = range ? [...range] : undefined;
  }

  public getBiomarkerName(): string {
    return this.biomarkerName;
  }

  public getData(): number[] {
    return this.data;
  }

  public getSliceIndex(): string {
    return this.sliceIndex;
  }

  public getRange(): [number, number] | undefined {
    if (!this.range) {
      const cleanData = this.data.filter(
        val => typeof val === 'number' && Number.isFinite(val),
      );
      this.range
        = cleanData.length > 0
          ? [Math.min(...cleanData), Math.max(...cleanData)]
          : undefined;
    }
    return this.range;
  }

  private getTerritoryData(territory: OCGTerritory): number[] {
    if (!this.data || this.data.length === 0) {
      return [];
    }
    const n = this.data.length;
    if (territory.toUpperCase() === 'GLOBAL') {
      return this.data;
    }
    if (n % 6 !== 0) {
      return [];
    }
    const k = n / 6;
    switch (territory.toUpperCase()) {
      case 'LAD':
        return this.data.slice(0, k).concat(this.data.slice(n - k));
      case 'RCA':
        return this.data.slice(k, 3 * k);
      case 'LCX':
        return this.data.slice(3 * k, 5 * k);
      default:
        return [];
    }
  }

  public getTerritoryMean(territory: OCGTerritory): {
    result: number;
    std: number;
  } {
    const arr = this.getTerritoryData(territory).filter(
      val => typeof val === 'number' && Number.isFinite(val),
    );
    if (arr.length === 0) {
      return { result: 0, std: 0 };
    }
    const mean = arr.reduce((sum, val) => sum + val, 0) / arr.length;
    const variance
      = arr.reduce((sum, val) => sum + (val - mean) ** 2, 0) / arr.length;
    const std = Math.sqrt(variance);
    return { result: mean, std };
  }

  public getTerritoryMedian(territory: OCGTerritory): {
    result: number;
    std: number;
  } {
    const arr = this.getTerritoryData(territory).filter(
      val => typeof val === 'number' && Number.isFinite(val),
    );
    if (arr.length === 0) {
      return { result: 0, std: 0 };
    }
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median
      = sorted.length % 2 !== 0
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;

    const mean = arr.reduce((sum, val) => sum + val, 0) / arr.length;
    const variance
      = arr.reduce((sum, val) => sum + (val - mean) ** 2, 0) / arr.length;
    const std = Math.sqrt(variance);

    return { result: median, std };
  }

  public getSegmentValue(segment: number): number {
    return this.data && segment >= 0 && segment < this.data.length
      ? this.data[segment]
      : 0;
  }

  public getMax(): number {
    const clean = this.data
      ? this.data.filter(
          val => typeof val === 'number' && Number.isFinite(val),
        )
      : [];
    return clean.length > 0 ? Math.max(...clean) : 0;
  }

  public getMin(): number {
    const clean = this.data
      ? this.data.filter(
          val => typeof val === 'number' && Number.isFinite(val),
        )
      : [];
    return clean.length > 0 ? Math.min(...clean) : 0;
  }
}
